<script setup lang="ts">
import { ButtonMain } from '@caks/uikit/button'
import { FoodCartItem } from '@caks/uikit/food'
import { ISeparatedItem, IContentWrapper, ICartData } from '@caks/shared/modules/i'
import { InputTextarea } from '@caks/uikit/input'
import { useAccountStore } from '@caks/shared/store/store.account'
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { CartItem, PromoCode } from '@caks/shared/types/types.user'
import { usePromoDiscount } from '@caks/shared/hooks/usePromo'
import { orderEstimate } from '../../api/user.route'
import { estimateErrors } from '@caks/shared/utils/estimateErrors'
import { isPlatform, toastController } from '@ionic/vue'
import { Keyboard } from '@capacitor/keyboard'
import { Capacitor } from '@capacitor/core'

interface Emit {
  (emit: 'close'): void
  (emit: 'open-promo'): void
  (emit: 'open-address'): void
  (emit: 'make-order', promo: string | null, comment: string): void
}

const props = withDefaults(defineProps<{ desktop?: boolean }>(), {
  desktop: false,
})
const emit = defineEmits<Emit>()

const router = useRouter()
const store = useAccountStore()

const keyboardHeight = ref('0px')

const cartItems = computed(() => store.cart)
const promocode = ref<null | PromoCode>(null)
const comment = ref('')
const addressNotValid = ref(false)

const removeFromCart = (id: number) => {
  store.removeFromCart(id)
}

const makeOrder = () => {
  if (!store.selectedLocation) {
    errorText.value = 'Адрес доставки не выбран'
    return
  }

  emit('make-order', promocode.value?.code || null, comment.value)
}

const goToMenu = () => {
  props.desktop ? emit('close') : router.push('/')
}

const openPromoInput = () => {
  if (props.desktop) {
    emit('open-promo')
  }
}

const openAddressPicker = () => {
  if (props.desktop) {
    emit('open-address')
  }
}

const changeAmount = (id: number, type: 'increase' | 'decrease') => {
  store.changeCartItemAmount(id, type)
}

const addPromo = (promo: PromoCode) => {
  promocode.value = promo
}

const { totalPrice, sale } = usePromoDiscount()

const isButtonDisabled = ref(true)
const errorText = ref('')

const getPreparedCartToEstimate = (items: CartItem[]) => {
  return items.map((el) => {
    return {
      id: el.item.id,
      count: el.amount,
      excluded_ingredients: el.removedModificators,
      base_modifications: el.baseModificators.map((el) => {
        return { id: el.modification_id, ingredient_id: el.variant_id }
      }),
      extra_modifications: el.extraModificators,
    }
  })
}

const getEstimateResult = async () => {
  const isDelivery = localStorage.getItem('order') === 'delivery'
  const lsLocation = JSON.parse(localStorage.getItem('location') || '{}')

  const items = getPreparedCartToEstimate(store.cart)

  let pointIdKey = isDelivery ? 'address_id' : 'pickup_point_id'

  if (lsLocation.title === 'Выбрать адрес доставки') {
    pointIdKey = 'pickup_point_id'
  }

  const payload: any = {
    ...store.orderInfo,
    items,
    [pointIdKey]: store.selectedLocation?.id || 0,
    promocode: store.promo?.code || null,
    with_stoplists: true,
  }

  const { data, status, code } = await orderEstimate(payload)

  if (status === 'ERR') {
    if (code === 80) {
      const toast = await toastController.create({
        message: 'Адрес доставки не найден',
        duration: 2000,
        position: 'top',
      })
      await toast.present()
      addressNotValid.value = true
    } else {
      addressNotValid.value = false
    }

    throw new Error(
      JSON.stringify({
        code: code.toString(),
        data,
      })
    )
  } else {
    addressNotValid.value = false
  }

  return data
}

const addressNotSeceted = () => {
  let result = false

  const lsLocationTitle = JSON.parse(localStorage.getItem('location') || '{}')

  if (lsLocationTitle.title === 'Выбрать адрес доставки' || addressNotValid.value) {
    result = true
  }

  return result
}

const buttoonIsDisabled = () => {
  if (addressNotSeceted()) {
    return true
  }

  return store.isEstimateLoading || errorText.value !== ''
}

const makeEstimate = async () => {
  try {
    store.estimate = null
    store.isEstimateLoading = true
    const estimateInfo = await getEstimateResult()

    store.setEstimate(estimateInfo)

    store.isEstimateLoading = false
    isButtonDisabled.value = false

    errorText.value = ''
  } catch (error) {
    const errorInfo = (error as Error).message

    const { code, data } = JSON.parse(errorInfo)

    // if (estimateErrors.hasOwnProperty(code)) {
    //   errorText.value = estimateErrors[code as keyof typeof estimateErrors] as string
    // }

    errorText.value = ''
    if (code == 22) {
      data.items.forEach((item: string) => {
        if (estimateErrors.hasOwnProperty(code)) {
          //errorText.value = estimateErrors[code as keyof typeof estimateErrors] as string
          //errorText.value += `\n (${item})`
          console.log('ffff')
          errorText.value += `${item} - ${estimateErrors[code as keyof typeof estimateErrors] as string} \n`
        }
      })
    }

    store.isEstimateLoading = false
  }
}

watch(
  () => store.cart,
  () => makeEstimate(),
  { deep: true }
)

watch(
  () => store.selectedLocation,
  () => makeEstimate()
)

watch(
  () => store.orderInfo,
  () => {
    makeEstimate()
  },
  { deep: true }
)

watch(
  () => store.promo,
  (value) => {
    if (value === undefined) {
      errorText.value = ''
    }
    makeEstimate()
  },
  { deep: true }
)

watch(comment, () => {
  store.setCartComment(comment.value)
})

onMounted(() => {
  if (props.desktop) {
    makeEstimate()
  }

  if (store.selectedLocation && store.orderInfo) {
    makeEstimate()
  }

  comment.value = store.cartComment

  if (Capacitor.isNativePlatform()) {
    Keyboard.addListener('keyboardWillShow', (info) => {
      keyboardHeight.value = info.keyboardHeight - 100 + 'px'
    })

    Keyboard.addListener('keyboardWillHide', () => {
      keyboardHeight.value = 0 + 'px'
    })
  }
})
</script>

<template>
  <i-content-wrapper
    is-cart
    class="cart"
    :class="{ cart_desktop: desktop, cart_mobile: !desktop }"
    :is-not-fixed-height="!desktop"
    :desktop="desktop"
    :style="`bottom: 0px; transition: bottom 0.2s cubic-bezier(0.17, 0.59, 0.4, 0.77); border-radius: 16px 16px 0px 0px; overflow: hidden;`"
  >
    <div class="cart__wrapper">
      <!-- Пустая корзина -->
      <div v-if="!cartItems.length" class="cart__empty empty">
        <img src="/assets/images/mock/empty-cart.png" alt="empty-cart-image" class="empty__image" />
        <h1 class="empty__title">Ой, пусто</h1>
        <div class="empty__subtitle">
          Ваша корзина пуста, откройте «Меню» и выберите понравившийся товар.
        </div>
        <button-main @click="goToMenu" class="empty__button">
          <template v-slot:default>Перейти в меню</template>
        </button-main>
      </div>

      <!-- НЕ пустая корзина -->
      <div class="cart__full" v-else>
        <slot v-if="!desktop" />
        <i-separated-item
          v-for="(item, index) of cartItems"
          :first="index === 0"
          :last="index === cartItems.length - 1"
          style="padding: 0 16px"
        >
          <food-cart-item @remove="removeFromCart" @change-count="changeAmount" :food="item" />
        </i-separated-item>
        <input-textarea
          v-model="comment"
          placeholder="Ваш комментарий здесь..."
          style="margin: 16px"
        >
        </input-textarea>
        <i-cart-data
          :total-price="totalPrice.sum"
          :desktop="desktop"
          :error-text="errorText"
          :address-not-selected="addressNotSeceted()"
          @open-promo="openPromoInput"
          @open-address="openAddressPicker"
          @after-validate-promo="addPromo"
        />
        <div style="padding: 16px" class="data__button" :class="{ data__button_desktop: desktop }">
          <div v-if="desktop && totalPrice.delivery" class="data__delivery data__flex">
            <p>Стоимость доставки заказа</p>
            <p>{{ totalPrice.delivery }}₽</p>
          </div>
          <div v-if="desktop && totalPrice.sum" class="data__price data__flex">
            <p>Стоимость заказа</p>
            <p>{{ totalPrice.sale > 0 ? totalPrice.sum - totalPrice.sale : totalPrice.sum }}₽</p>
          </div>
          <div style="display: grid">
            <button-main :disabled="buttoonIsDisabled()" @click="makeOrder">
              <template v-if="!store.isEstimateLoading" #default>
                Оплатить
                {{ totalPrice.sum - totalPrice.sale + totalPrice.delivery }}₽
              </template>
              <template v-else #default>
                <skeletor style="width: 64px" />
              </template>
            </button-main>
          </div>
        </div>
      </div>
    </div>
  </i-content-wrapper>
</template>

<style scoped lang="scss">
.cart {
  &_mobile {
    padding: 0;
  }

  &__wrapper {
    height: 100%;
  }

  &__full {
    position: relative;
    height: calc(100%);

    display: flex;
    flex-direction: column;
    background-color: $bg-gray;
    overflow-y: auto;
    overflow-x: hidden;

    overscroll-behavior: none;

    .cart_mobile & {
      height: calc(
        100dvh - 140px - env(safe-area-inset-bottom) - env(safe-area-inset-top) -
          (v-bind('keyboardHeight'))
      );
    }
  }

  &__address {
    display: flex;
    align-items: center;
    justify-content: space-between;

    @include h4-4;
    color: $primary-text;
    margin: 16px 0;
  }
}
.data {
  @include h4-4;

  &__price {
    @include h3-3;
    color: $primary-text;
    margin-top: auto;
  }

  &__delivery {
    @include h5-2;
    color: $primary-text;
    margin-top: auto;
  }

  &__button {
    display: flex;
    flex-direction: column;
    gap: 16px;
    position: sticky;
    bottom: 0;
    left: 0;

    &_desktop {
      background-color: #fafafa;
    }
  }

  &__flex {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
  }
}

.empty {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  text-align: center;
  padding: 0 16px;
  background: transparent;

  &__image {
    width: 100%;
    display: flex;
    justify-content: center;

    img {
      max-width: 100%;
    }
  }

  &__title {
    @include h2-1;
    color: $primary-text;
  }

  &__subtitle {
    @include h5-6;
    color: $sub-text;
  }

  &__button {
    width: 100%;
  }
}

.cart_desktop {
  .cart__wrapper {
    min-height: 100%;
    height: 1px;
  }

  .cart__items {
    max-height: 80%;
    overflow-y: scroll;
    padding-right: 8px;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: $ui-gray-2;
      border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: $sub-text;
      border-radius: 10px;
      border: 2px solid $ui-gray-2;
    }
  }
}
</style>
